import { useEffect, useRef, useState } from 'react'

import { useLocation, useNavigate } from '@muse-web/router'

import { getUploadFailureParams, sendEvent } from '@core/analytics'
import { uploadFile, sendFailedUploadMessage, sendSucceededUploadMessage } from '@core/upload'
import { HttpStatusCode, isHttpError } from '@core/http-client'
import { Logger } from '@core/logger'
import { resetAllAuthorAudios } from '@features/audio'

import { createAudioSource } from '../services/createAudioSource'
import { deleteAudioSource } from '../services/deleteAudioSource'
import { useSourceTranscodings } from './useSourceTranscodings'

const ABORT_MESSAGE = 'Upload aborted'

/**
 * How long to wait for the new source to become playable before giving up and showing the audio as
 * it currently is. Transcoding normally lands in seconds; without a bound the hero would sit on
 * "processing" forever if the pipeline stalls.
 */
const PROCESSING_TIMEOUT_MS = 2 * 60 * 1000

/** Why a replace failed. The container maps these to copy — the hook stays free of i18n. */
export type TReplaceSourceError = 'invalid-file' | 'not-found' | 'unauthorized' | 'generic'

type TUseReplaceAudioSourceParams = {
    audioId: string
    authorUsername: string
    onError?: (reason: TReplaceSourceError) => void
    /** Called when the new source never became playable in time. */
    onProcessingTimeout?: () => void
}

const toCreateSourceError = (error: unknown): TReplaceSourceError => {
    if (!isHttpError(error)) {
        return 'generic'
    }

    if (error.response.status === HttpStatusCode.UNPROCESSABLE_ENTITY) {
        return 'invalid-file'
    }

    if (error.response.status === HttpStatusCode.NOT_FOUND) {
        return 'not-found'
    }

    if (error.response.status === HttpStatusCode.UNAUTHORIZED) {
        return 'unauthorized'
    }

    return 'generic'
}

const runCleanupStep = async (
    step: () => Promise<unknown>,
    payload: { audioId: string; createdSourceId: string },
) => {
    try {
        await step()
    } catch (error) {
        Logger.error('Replace audio source cleanup error:', { error, payload })
    }
}

/**
 * Replaces an audio's source file: create source → upload → tell the backend the upload succeeded.
 * That last ping is what triggers transcoding — without it the new file silently never appears.
 *
 * The screen's audio comes from immutable loader data, so once the new source has a playable
 * transcoding we re-request the loader (soft `replace` navigation) instead of patching state. We poll
 * first because the player has no disabled state for an empty `transcodingUrl`.
 */
export const useReplaceAudioSource = ({
    audioId,
    authorUsername,
    onError,
    onProcessingTimeout,
}: TUseReplaceAudioSourceParams) => {
    const navigate = useNavigate()
    const { href } = useLocation()
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [progress, setProgress] = useState<number | null>(null)
    const [uploadedSourceId, setUploadedSourceId] = useState('')

    const isUploading = progress !== null
    const isProcessing = progress === 100
    const { data: transcodings } = useSourceTranscodings(isProcessing ? uploadedSourceId : '', {
        pollUntilReady: true,
    })
    const isSourceReady = Boolean(transcodings?.some(transcoding => transcoding.url !== ''))

    const stopProcessing = () => {
        setProgress(null)
        setUploadedSourceId('')
    }

    useEffect(() => {
        if (!isProcessing || !isSourceReady) {
            return undefined
        }

        stopProcessing()
        navigate(href, { replace: true })

        return undefined
    }, [isProcessing, isSourceReady, href, navigate])

    useEffect(() => {
        if (!isProcessing || isSourceReady) {
            return undefined
        }

        const timeoutId = setTimeout(() => {
            Logger.error('Replace audio source processing timeout:', {
                payload: { audioId, uploadedSourceId },
            })
            stopProcessing()
            onProcessingTimeout?.()
        }, PROCESSING_TIMEOUT_MS)

        return () => {
            clearTimeout(timeoutId)
        }
        // `onProcessingTimeout` is a fresh closure each render; the timer must not restart on it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isProcessing, isSourceReady, audioId, uploadedSourceId])

    const openFilePicker = () => {
        fileInputRef.current?.click()
    }

    const replaceSource = async (file: File) => {
        if (isUploading) {
            return
        }

        setProgress(0)

        let policy: Awaited<ReturnType<typeof createAudioSource>> | undefined
        let createdSourceId = ''

        try {
            policy = await createAudioSource({
                id: audioId,
                mime: file.type,
                name: file.name,
                size: file.size,
                method: 'PUT',
            })
            createdSourceId = policy.extra.source.id
        } catch (error) {
            setProgress(null)
            Logger.error('Create audio source error:', { error, payload: { audioId } })
            onError?.(toCreateSourceError(error))

            return
        }

        try {
            await uploadFile({
                file,
                policy,
                onProgress: value => {
                    const percentage = Math.round(value * 100)

                    // 100% is reserved for "upload acknowledged", so cap the transfer at 99%.
                    setProgress(percentage === 100 ? 99 : percentage)
                },
            })

            await sendSucceededUploadMessage(policy.success)
            await resetAllAuthorAudios({ username: authorUsername })

            sendEvent({ name: 'upload_audio_successful', payload: { audio: audioId } })

            setUploadedSourceId(createdSourceId)
            setProgress(100)
        } catch (error) {
            setProgress(null)

            if (error instanceof Error && error.message === ABORT_MESSAGE) {
                return
            }

            Logger.error('Replace audio source error:', { error, payload: { audioId } })

            // Each cleanup step stands alone: a 401 makes both throw, and a failing fail-ping must
            // not leave the created source orphaned.
            await runCleanupStep(() => sendFailedUploadMessage(policy.fail), {
                audioId,
                createdSourceId,
            })
            await runCleanupStep(() => deleteAudioSource(createdSourceId), {
                audioId,
                createdSourceId,
            })

            sendEvent({
                name: 'upload_audio_unsuccessful',
                payload: await getUploadFailureParams(error),
            })
            onError?.('generic')
        }
    }

    return { fileInputRef, openFilePicker, replaceSource, progress, isUploading, isProcessing }
}
